import React from 'react';
import { useDispatch } from 'react-redux';
import { setUpdateMyDocumentsList } from 'reducers/misc';
import { PopupOrPanel } from '../../components/popupsAndPanels/PopupAndPanels';
import { Spinner } from 'components/spinner/Spinner';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import FormTextField from 'components/formComponents/FormTextField';
import * as Yup from 'yup';
import { endpoints } from 'endpoints.config';
import { MyDocumentsApi } from 'api/myDocumentsApi';
import { ErrorM } from 'components/formComponents/ErrorM';
import { useFetch } from 'api';

type Props = {
  panelOpen: boolean;
  togglePanelOpen: React.Dispatch<React.SetStateAction<boolean>>;
  selectedId?: number;
};

type UpdateFolderResponse = {
  details: {
    folderName: string;
  };
  id: number;
};

export const EditFolder: React.FC<Props> = ({
  panelOpen,
  togglePanelOpen,
  selectedId,
}) => {
  const formikRef = React.useRef<FormikProps<any>>(null);

  const dispatch = useDispatch();

  const { loading, data, error } = useFetch<UpdateFolderResponse>(
    endpoints.myDocuments.updateFolder,
    {
      params: { id: selectedId },
    }
  );

  const validationSchema = Yup.object({
    folderName: Yup.string().required('Folder name is required'),
  });

  const submitForm = async (values: any, formikProps: FormikHelpers<any>) => {
    try {
      const result = await MyDocumentsApi.createEditFolder(
        endpoints.myDocuments.updateFolder,
        values
      );

      if (result.data && result.data.status === '1') {
        dispatch(setUpdateMyDocumentsList(true));
        togglePanelOpen(false);
      }

      if (result.data && result.data.errors) {
        formikProps.setFieldError('name', result.data.errors[0].message);
      }
      if (result.error) {
        formikProps.setFieldError('genericError', result.error);
      }
    } catch (error) {
      formikProps.setFieldError('genericError', 'An error occurred');
    } finally {
      formikProps.setSubmitting(false);
    }
  };

  return (
    <PopupOrPanel
      isPopup={true}
      isOpen={panelOpen}
      hdrTitle="Edit folder"
      position="right"
      setTop={true}
      noBackground={true}
      noHeader={true}
      noContentContainer={true}
      givenClass="DocumentHandlePage"
      noFooter={true}
      handleClose={() => togglePanelOpen(false)}
      handleSecondaryBtn={() => togglePanelOpen(false)}
      width={560}
      isSubmitting={formikRef.current?.isSubmitting}
      noOverflowToggle={true}
    >
      {loading && !error && <Spinner />}
      {!loading && data && (
        <Formik
          innerRef={formikRef}
          initialValues={{
            folderName: data.details.folderName,
            foldersId: data.id,
          }}
          onSubmit={submitForm}
          validationSchema={validationSchema}
          enableReinitialize
        >
          {({ submitForm, isSubmitting, values }) => (
            <Form>
              <div className="WidthContent">
                <span
                  className="BtnBack"
                  onClick={() => togglePanelOpen(false)}
                >
                  <span className="BB_Icon"></span>
                  <span className="BB_Txt">Back</span>
                </span>

                <div className="DHPageContent">
                  <h2>Edit folder</h2>
                  <div
                    className={
                      'DocDetailsRow' + (isSubmitting ? ' DisableDocForm' : '')
                    }
                  >
                    <div className="DocDRInner">
                      <div className={'DocRowBlock'}>
                        <FormTextField
                          label="Folder name"
                          field="folderName"
                          required={true}
                          labelPosition="left"
                          value={values.folderName}
                          placeholder="Folder name"
                        />
                        <ErrorM name="genericError" />
                      </div>
                    </div>
                  </div>

                  <div
                    className="Btn BtnOrange DocFormSubmitBtn"
                    onClick={() => submitForm()}
                  >
                    Save
                  </div>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      )}
      {formikRef.current?.isSubmitting && <Spinner />}
    </PopupOrPanel>
  );
};
